import { useGetDailyIdleTimeAbuseQuery } from '@/app/features/employee/management-dashboard/management-dashboard.api';
import useError from '@/hooks/useError';
import type { ManagementFilters } from '@/hooks/useManagementFilters';
import { formatDateRange } from '@/utils';
import { Flex } from 'antd';
import dayjs from 'dayjs';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

const CustomTooltip = (props: any) => {
  const { active, payload, label } = props;

  if (active && payload && payload.length) {
    return (
      <div className="bg-[#0F172A] text-white p-2 rounded border border-[#1f2d40] text-xs">
        <p className="font-bold mb-1">{dayjs(label).format('D- MMM')}</p>
        <div className="flex items-center gap-1.5">
          <div className="size-2 rounded-full bg-[#52D1F2]" />
          <p className="text-white">+{payload[0].value?.toFixed(1)} h</p>
        </div>
      </div>
    );
  }

  return null;
};

const BreakTimeAbuse = ({
  department_id,
  office_id,
  filter,
}: {
  department_id?: string;
  office_id?: string;
  filter: ManagementFilters;
}) => {
  const breakFilter = filter.workForceCulture;

  const { data: abuseData, error } = useGetDailyIdleTimeAbuseQuery({
    start_date: breakFilter.start_date || '',
    end_date: breakFilter.end_date || '',
    department_id,
    office_id,
  });
  useError(error, 'Failed to fetch break time abuse stats');

  const chartData = (abuseData?.data?.data?.daily_breakdown || []).map(
    (item) => ({
      name: item.date,
      value: item.total_idle_hours,
    }),
  );

  return (
    <div className="bg-theme-slate-900 border border-theme-gray-700 p-4 rounded-xl w-full">
      <Flex
        justify="space-between"
        align="center"
        className="mb-6 text-white p-2"
      >
        <h4 className="text-xl font-medium text-white">Break Time Abuse</h4>
        <span className="text-theme-primary text-sm">
          {formatDateRange(breakFilter.start_date, breakFilter.end_date, {
            separator: '-',
          })}
        </span>
      </Flex>

      <ResponsiveContainer width="100%" height={250}>
        <BarChart
          data={chartData}
          margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
        >
          <CartesianGrid
            strokeDasharray="8 8"
            stroke="#1e2a38"
            vertical={false}
          />
          <XAxis
            dataKey="name"
            stroke="#94a3b8"
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 14, fill: '#ffffff' }}
            tickFormatter={(val) => dayjs(val).format('D -MMM')}
            dy={10}
          />
          <YAxis
            stroke="#94a3b8"
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 12, fill: '#ffffff' }}
            tickFormatter={(value) => `+${value} h`}
          />
          <Tooltip
            content={<CustomTooltip />}
            cursor={{ fill: 'transparent' }}
          />
          <Bar
            dataKey="value"
            fill="#52D1F2"
            radius={[6, 6, 0, 0]}
            barSize={45}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default BreakTimeAbuse;
